/**
 * Multipart (chunked, resumable) uploader for files >100MB.
 * Uses presigned PUT URLs per part directly against S3.
 */
import axios from 'axios';
import { dataRoomAPI } from '../../../services/api';

const PART_SIZE = 8 * 1024 * 1024; // 8MB per part
export const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100MB

export async function multipartUpload({ dataRoomId, file, folderId, onProgress }) {
  // 1. init
  const init = await dataRoomAPI.multipartInit(dataRoomId, {
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    folder_id: folderId || null,
    size: file.size,
  });
  const { upload_id: uploadId } = init.data;

  const totalParts = Math.ceil(file.size / PART_SIZE);
  const parts = [];
  let bytesSent = 0;

  try {
    for (let i = 1; i <= totalParts; i++) {
      const start = (i - 1) * PART_SIZE;
      const end = Math.min(start + PART_SIZE, file.size);
      const blob = file.slice(start, end);

      const urlRes = await dataRoomAPI.multipartPartUrl(dataRoomId, uploadId, i);
      const putUrl = urlRes.data.url;

      const resp = await axios.put(putUrl, blob, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        onUploadProgress: (evt) => {
          const sent = bytesSent + (evt.loaded || 0);
          const pct = Math.min(100, Math.round((sent * 100) / file.size));
          onProgress && onProgress(pct);
        },
      });
      const etag = (resp.headers['etag'] || resp.headers['ETag'] || '').replace(/"/g, '');
      parts.push({ PartNumber: i, ETag: `"${etag}"` });
      bytesSent += blob.size;
      onProgress && onProgress(Math.round((bytesSent * 100) / file.size));
    }

    const completed = await dataRoomAPI.multipartComplete(dataRoomId, uploadId, parts);
    return completed.data;
  } catch (err) {
    try { await dataRoomAPI.multipartAbort(dataRoomId, uploadId); } catch (_) { /* ignore */ }
    throw err;
  }
}
